import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'react-fast-compare';
import cx from 'classnames';
import _ from 'lodash';
import { Intent, Button, MenuItem } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { MultiSelect } from '@blueprintjs/select';

import cls from './TagSelect.module.css';

function defaultLabelRenderer(groupedItemIds) {
  return `${groupedItemIds.length} item${groupedItemIds.length !== 1 ? 's' : ''} selected`;
}

const defaultTagProps = {
  intent: Intent.NONE,
};

const defaultGroup = {
  collapseThreshold: null,
  tagProps: defaultTagProps,
  labelRenderer: defaultLabelRenderer,
};

function buildGroup(group) {
  return _.merge({}, defaultGroup, group);
}

export default class extends PureComponent {
  static propTypes = {
    'items': PropTypes.array,
    'initialItemIds': PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    'selectedItemIds': PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    'idField': PropTypes.string,
    'labelField': PropTypes.string,
    'groups': PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        intent: PropTypes.string,
        collapseThreshold: PropTypes.number,
        labelRenderer: PropTypes.func,
      })
    ),
    'groupBy': PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    'allowEmptyItemRange': PropTypes.bool,
    'large': PropTypes.bool,
    'enableSelectAll': PropTypes.bool,
    'enableSelectNone': PropTypes.bool,
    'menuItemProps': PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    'data-test': PropTypes.string,
    'onChange': PropTypes.func,
    'onItemRangesChange': PropTypes.func,
    'onEmptyItemRangeRemove': PropTypes.func,
  };

  static defaultProps = {
    initialItemIds: [],
    idField: 'id',
    labelField: 'label',
    groups: [],
    allowEmptyItemRange: false,
    large: false,
    enableSelectAll: false,
    enableSelectNone: false,
    menuItemProps: {},
    onChange: () => {},
    onItemRangesChange: () => {},
    onEmptyItemRangeRemove: () => {},
  };

  state = {
    selectedItems: [],
    selectedItemRanges: [],
  };

  componentDidMount() {
    this.updateItems();
  }

  componentDidUpdate(prevProps, prevState) {
    const { items, selectedItemIds } = this.props;
    const { items: prevItems, selectedItemIds: prevSelectedItemIds } = prevProps;
    const { selectedItems, selectedItemRanges } = this.state;
    const { selectedItems: prevSelectedItems, selectedItemRanges: prevSelectedItemRanges } = prevState;

    const didItemsChange = items && !isEqual(items, prevItems);
    const didSelectedItemIdsChange = Array.isArray(selectedItemIds) && !isEqual(selectedItemIds, prevSelectedItemIds);
    const didSelectedItemsChange = !isEqual(selectedItems, prevSelectedItems);
    const didSelectedItemRangesChange = !isEqual(selectedItemRanges, prevSelectedItemRanges);

    if (didItemsChange) {
      this.updateItems();
    }

    if (didSelectedItemIdsChange) {
      this.updateSelectedItems();
    }

    if (didSelectedItemsChange) {
      this.props.onChange(selectedItems);
    }

    if (didSelectedItemRangesChange) {
      this.props.onItemRangesChange(selectedItemRanges);
    }
  }

  updateItems = () => {
    const { initialItemIds, selectedItemIds, items, idField } = this.props;
    const { selectedItems } = this.state;

    const itemIdsToSelect = Array.isArray(selectedItemIds) ? selectedItemIds : initialItemIds;

    const nextSelectedItems = _.chain(items)
      .filter(item => itemIdsToSelect.includes(item[idField]))
      .concat(selectedItems)
      .uniqBy(idField)
      .filter(selectedItem => _.find(items, [idField, selectedItem[idField]]))
      .value();

    this.updateSelectionState(nextSelectedItems);
  };

  updateSelectedItems = () => {
    const { selectedItemIds, items, idField } = this.props;
    const { selectedItems } = this.state;

    const shouldUpdate =
      selectedItems.length !== selectedItemIds.length ||
      selectedItems.some(item => !selectedItemIds.includes(item[idField]));

    if (!shouldUpdate) {
      return;
    }

    const nextSelectedItems = _.chain(items)
      .filter(item => selectedItemIds.includes(item[idField]))
      .uniqBy(idField)
      .value();

    this.updateSelectionState(nextSelectedItems);
  };

  updateSelectionState = nextSelectedItems => {
    this.setState({ ...this.buildSelectionState(nextSelectedItems) });
  };

  buildMenuItemProps = (item, modifiers) => {
    const { menuItemProps } = this.props;

    if (typeof menuItemProps === 'function') {
      return menuItemProps(item, modifiers);
    }

    return menuItemProps;
  };

  getGroupId = item => {
    const { groupBy } = this.props;

    if (typeof groupBy === 'function') {
      const groupId = groupBy(item);
      return item[groupId];
    }

    return item[groupBy];
  };

  getItemIdx = id => {
    const { idField } = this.props;
    const { selectedItems } = this.state;
    return selectedItems.findIndex(item => item[idField] === id);
  };

  isItemSelected = id => {
    return this.getItemIdx(id) >= 0;
  };

  filterItem = (query, item) => {
    const { labelField } = this.props;

    const normalizedText = item[labelField].toLowerCase();
    const normalizedQuery = query.toLowerCase();

    return normalizedText.indexOf(normalizedQuery) >= 0;
  };

  getTagProps = (text, itemRangeIdx) => {
    const { groups, large, 'data-test': dataTest } = this.props;
    const { selectedItemRanges } = this.state;
    const { indices, groupId } = selectedItemRanges[itemRangeIdx];

    const group = _.find(groups, ['id', groupId]);
    const { tagProps } = buildGroup(group);

    const result = {
      round: true,
      minimal: true,
      ...tagProps,
      large,
    };

    if (indices.length === 1) {
      result['data-test'] = `${dataTest}-single-item-tag`;
    } else {
      result['data-test'] = `${dataTest}-multiple-items-tag`;
    }

    return result;
  };

  buildSelectionState = nextSelectedItems => {
    const { items, groups, allowEmptyItemRange } = this.props;
    const { selectedItemRanges } = this.state;

    const singleItemRanges = nextSelectedItems.map((item, idx) => ({
      indices: [idx],
      groupId: this.getGroupId(item),
    }));

    if (groups.length === 0) {
      return {
        selectedItems: nextSelectedItems,
        selectedItemRanges: singleItemRanges,
      };
    }

    const activeGroups = _.countBy(items, item => this.getGroupId(item));

    const nextSelectedItemRanges = _.chain(groups)
      .map(rawGroup => {
        const group = buildGroup(rawGroup);

        if (!activeGroups[group.id]) {
          return [];
        }

        const itemRanges = _.filter(singleItemRanges, ['groupId', group.id]);

        if (allowEmptyItemRange && itemRanges.length === 0) {
          const prevItemRanges = _.filter(selectedItemRanges, ['groupId', group.id]);

          if (prevItemRanges.length === 1 && prevItemRanges[0].indices.length === 0) {
            return [
              {
                indices: [],
                groupId: group.id,
              },
            ];
          }
        }

        if (group.collapseThreshold == null || itemRanges.length <= group.collapseThreshold) {
          return itemRanges;
        }

        return [
          {
            indices: _.chain(itemRanges).map('indices').flatten().value(),
            groupId: group.id,
          },
        ];
      })
      .flatten()
      .value();

    return {
      selectedItems: nextSelectedItems,
      selectedItemRanges: nextSelectedItemRanges,
    };
  };

  onItemSelect = item => {
    const { idField } = this.props;

    if (this.isItemSelected(item[idField])) {
      const idx = this.getItemIdx(item[idField]);
      this.onItemDeselect(idx);
      return;
    }

    const { selectedItems } = this.state;

    const nextSelectedItems = _.uniqBy([...selectedItems, item], idField);

    this.updateSelectionState(nextSelectedItems);
  };

  onItemsPaste = items => {
    const { idField } = this.props;
    const { selectedItems } = this.state;

    const nextSelectedItems = _.uniqBy([...selectedItems, ...items], idField);

    this.updateSelectionState(nextSelectedItems);
  };

  onItemDeselect = idx => {
    const { selectedItems } = this.state;

    const nextSelectedItems = selectedItems.filter((g, selectedIdx) => selectedIdx !== idx);

    this.updateSelectionState(nextSelectedItems);
  };

  onItemRangeDeselect = (text, rangeIdx) => {
    const { allowEmptyItemRange } = this.props;
    const { selectedItems, selectedItemRanges } = this.state;
    const { indices, groupId } = selectedItemRanges[rangeIdx];

    if (indices.length === 0) {
      this.setState(
        {
          selectedItemRanges: selectedItemRanges.filter((range, idx) => idx !== rangeIdx),
        },
        () => {
          this.props.onEmptyItemRangeRemove(groupId);
        }
      );
      return;
    }

    const nextSelectedItems = selectedItems.filter((item, idx) => !indices.includes(idx));
    const nextState = this.buildSelectionState(nextSelectedItems);

    if (allowEmptyItemRange) {
      nextState.selectedItemRanges.splice(rangeIdx, 0, {
        indices: [],
        groupId,
      });
    }

    this.setState({
      ...nextState,
    });
  };

  onClear = () => {
    this.updateSelectionState([]);
  };

  onSelectAll = () => {
    const { items } = this.props;
    this.updateSelectionState(items);
  };

  renderItem = (item, { modifiers, handleClick }) => {
    const { idField, labelField, 'data-test': dataTest } = this.props;

    if (!modifiers.matchesPredicate) {
      return null;
    }

    return (
      <MenuItem
        // active={modifiers.active}
        icon={this.isItemSelected(item[idField]) ? 'tick' : 'blank'}
        text={item[labelField]}
        {...this.buildMenuItemProps(item, modifiers)}
        shouldDismissPopover={false}
        data-cy={`${dataTest}-menu-item`}
        onClick={handleClick}
        key={item[idField]}
      />
    );
  };

  renderTag = itemRange => {
    const { labelField, groups } = this.props;
    const { selectedItems } = this.state;
    const { indices, groupId } = itemRange;

    const rawGroup = _.find(groups, ['id', groupId]);
    const group = buildGroup(rawGroup);

    if (indices.length === 1 && (group.collapseThreshold == null || group.collapseThreshold > 0)) {
      return selectedItems[indices[0]][labelField];
    }

    const { labelRenderer } = group;
    const groupedItemIds = indices.map(idx => selectedItems[idx][labelField]);

    return labelRenderer(groupedItemIds);
  };

  render() {
    const {
      items,
      initialItemIds,
      labelField,
      groups,
      enableSelectAll,
      enableSelectNone,
      large,
      'data-test': dataTest,
      onChange,
      ...restProps
    } = this.props;
    const { selectedItemRanges } = this.state;

    return (
      <MultiSelect
        fill
        {...restProps}
        items={items}
        selectedItems={selectedItemRanges}
        itemPredicate={this.filterItem}
        itemRenderer={this.renderItem}
        onItemSelect={this.onItemSelect}
        onItemsPaste={this.onItemsPaste}
        tagRenderer={this.renderTag}
        tagInputProps={{
          onRemove: this.onItemRangeDeselect,
          tagProps: this.getTagProps,
          rightElement: (
            <>
              {enableSelectAll && (
                <Tooltip2 content="Select all" placement="top">
                  <Button
                    className={cx({ [cls.large]: large })}
                    minimal
                    icon="multi-select"
                    onClick={this.onSelectAll}
                    data-test={`${dataTest}-select-all-button`}
                  />
                </Tooltip2>
              )}
              {enableSelectNone && (
                <Tooltip2 content="Select none" placement="top">
                  <Button
                    className={cx({ [cls.large]: large })}
                    minimal
                    icon="cross"
                    data-test={`${dataTest}-select-none-button`}
                    onClick={this.onClear}
                  />
                </Tooltip2>
              )}
            </>
          ),
        }}
        itemsEqual={labelField}
        noResults={<MenuItem disabled text="No matching results." data-test={`${dataTest}-empty-menu-item`} />}
        popoverProps={{
          minimal: true,
          placement: 'bottom',
        }}
      />
    );
  }
}
